import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { Loader as Loader2 } from 'lucide-react';
import { KioskService, KioskLeagueBranding } from '../services/KioskService';
import KioskShell from '../components/KioskShell';
import { useKioskBasePath } from '../hooks/useKioskBasePath';
import { TvFocusableInput } from '../components/TvFocusableInput';

function isLightColor(hex: string): boolean {
  const c = hex.replace('#', '');
  if (c.length < 6) return false;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 160;
}

export const KioskVerify: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const basePath = useKioskBasePath();
  const tmQuery = search.get('testmode') === 'true' ? '?testmode=true' : '';
  const [league, setLeague] = useState<KioskLeagueBranding | null>(null);
  const [code, setCode] = useState(search.get('dc') || '');
  const [email] = useState(search.get('email') || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        const d = await KioskService.getLeaderboard(slug);
        setLeague(d.league);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed');
      }
    })();
  }, [slug]);

  const doSubmit = async (codeValue: string) => {
    setError(null);
    if (!slug || !email) {
      setError('Missing email. Please restart sign-up.');
      return;
    }
    try {
      setSubmitting(true);
      await KioskService.verify(slug, email, codeValue.trim());
      navigate(`/${basePath}/${slug}/me${tmQuery}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    await doSubmit(code);
  };

  const handleCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6) {
      setTimeout(() => doSubmit(digits), 50);
    }
  };

  const resend = async () => {
    if (!slug || !email) return;
    setResending(true);
    setError(null);
    setInfo(null);
    try {
      const res = await KioskService.requestVerification(slug, email, 'Singer', false);
      setCode(res.debug_code || '');
      setInfo('A new code has been sent. Any unexpired code will still work.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  if (!league) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white/80">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const primary = league.primary_color || '#f97316';
  const accent = league.accent_color || '#f97316';

  return (
    <KioskShell league={league} leagueSlug={slug!}>
      <div className="max-w-md mx-auto">
        <div className="bg-white/5 backdrop-blur-md border border-white/15 rounded-2xl p-6 sm:p-8">
          <h1 className="text-2xl font-black text-center">Verify your email</h1>
          <p className="text-sm text-white/70 text-center mt-2 mb-6">
            We sent a 6-digit code to <span className="font-medium text-white">{email}</span>.
          </p>

          <form onSubmit={submit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-500/15 border border-red-400/30 rounded-lg text-red-200 text-sm">
                {error}
              </div>
            )}
            {info && (
              <div className="p-3 bg-emerald-500/15 border border-emerald-400/30 rounded-lg text-emerald-200 text-sm">
                {info}
              </div>
            )}
            <TvFocusableInput
              inputMode="numeric"
              enterKeyHint="done"
              maxLength={6}
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              onPaste={(e) => {
                const pasted = e.clipboardData.getData('text');
                const digits = pasted.replace(/\D/g, '').slice(0, 6);
                if (digits.length > 0) {
                  e.preventDefault();
                  handleCodeChange(digits);
                }
              }}
              className="w-full text-center text-3xl tracking-[0.5em] font-mono px-3 py-4 bg-black/30 border border-white/20 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:ring-2"
              placeholder="000000"
              style={{ boxShadow: `0 0 0 0 ${accent}` }}
            />
            <button
              type="submit"
              disabled={submitting || code.length !== 6}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold disabled:opacity-50 hover:brightness-110 transition shadow-lg"
              style={{ backgroundColor: primary, color: isLightColor(primary) ? '#1e293b' : '#ffffff' }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Continue'}
            </button>
            <div className="flex items-center justify-between text-xs pt-1">
              <button type="button" onClick={resend} disabled={resending} className="text-white/60 hover:text-white">
                {resending ? 'Sending...' : 'Resend code'}
              </button>
              <Link to={`/${basePath}/${slug}`} className="text-white/60 hover:text-white">
                Change email
              </Link>
            </div>
          </form>
        </div>
      </div>
    </KioskShell>
  );
};

export default KioskVerify;
